import React, { useState, useEffect } from 'react';
import { apiConfig } from '../firebase-config';
import { useReturnContext } from '../context/ReturnContext';
import './OrderLookup.css';

export default function OrderLookup({ onOrderFound }) {
  const { orderData, resetReturnState } = useReturnContext();
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // If we have existing order data in context, pre-fill the form
  useEffect(() => {
    if (orderData) {
      // If we already have order data but we're back at step 1,
      // pre-fill the form with the existing order number
      setOrderNumber(orderData.name ? orderData.name.replace('#', '') : '');
      setEmail(orderData.email || '');
    }
  }, [orderData]);

  const handleSubmit = async (e) => {
    // Ensure the default form submission behavior is prevented
    if (e && e.preventDefault) {
      e.preventDefault();
      console.log('Form submission prevented');
    }
    
    if (!orderNumber.trim()) {
      setError('Please enter your order number');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Remove any # character from the order number
      const cleanOrderNumber = orderNumber.replace('#', '');
      
      console.log('Fetching order with number:', cleanOrderNumber);
      
      // Call the Firebase function using the callFunction helper
      const functionUrl = `${apiConfig.baseUrl}${apiConfig.endpoints.getShopifyOrderByNumber}?number=${cleanOrderNumber}`;
      
      // Use a more robust approach to fetch with error handling
      let response;
      try {
        response = await fetch(functionUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        console.log('API response status:', response.status);
      } catch (fetchError) {
        console.error('Fetch error:', fetchError);
        throw new Error(`Network error: ${fetchError.message}`);
      }
      
      let data;
      try {
        data = await response.json();
        console.log('API response data:', data);
      } catch (jsonError) {
        console.error('JSON parsing error:', jsonError);
        throw new Error('Failed to parse response from server');
      }
      
      // Ensure the order data has proper price information
      if (data.order && data.order.line_items) {
        data.order.line_items = data.order.line_items.map(item => ({
          ...item,
          price: parseFloat(item.price) || 0
        }));
      }
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to find order');
      }
      
      if (!data.order) {
        throw new Error('Order not found');
      }
      
      // Pass the order data to the parent component
      onOrderFound(data.order);
    } catch (err) {
      console.error('Error fetching order:', err);
      setError(err.message || 'Failed to find your order. Please check your order number and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle button click instead of form submission
  const handleButtonClick = (e) => {
    // Prevent any default behavior
    e.preventDefault();
    e.stopPropagation();
    
    // Reset context states before searching for a new order
    resetReturnState();
    
    // Call the submit handler
    handleSubmit(e);
  };

  return (
    <div className="order-lookup">
      <h2 className="section-title">Find Your Order</h2>
      <p className="mb-6 text-gray-600">
        Enter your order number to start the return or exchange process.
      </p>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="orderNumber" className="label">
            Order Number*
          </label>
          <input
            id="orderNumber"
            type="text"
            className="input-field"
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            placeholder="e.g. #1001"
            required
          />
        </div>
        
        <div>
          <label htmlFor="email" className="label">
            Email (optional)
          </label>
          <input
            id="email"
            type="email"
            className="input-field"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
          />
          <small className="form-text">
            Providing your email will help us send you updates about your return or exchange.
          </small>
        </div>
        
        {error && <p className="error-text">{error}</p>}
        
        <div className="text-center">
          <button 
            type="button" 
            className="btn btn-primary"
            disabled={loading}
            onClick={handleButtonClick}
          >
            {loading ? (
              <><i className="fas fa-spinner fa-spin mr-2"></i> Finding Order...</>
            ) : (
              <><i className="fas fa-search mr-2"></i> Find My Order</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
